import React, { useRef, useEffect, Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import ReactGA from "react-ga";

import ScrollReveal from "./utils/ScrollReveal";
import LayoutDefault from "./layouts/LayoutDefault";

// Views
import Home from "./views/Home";
import Privacy from "./views/Privacy";
import Help from "./views/Help";
import Extension from "./views/Extension";
import BringMeBack from "./views/BringMeBack";
import NotFound from "./views/NotFound";
import { applyRouteMeta, handleRouteScroll } from "./marketing/routeMeta";

// The "10X" focus dashboard is a fully self-contained SPA under /app/*. It is loaded
// lazily so its bundle (incl. recharts) never weighs down the marketing pages, and it
// renders OUTSIDE the marketing LayoutDefault/ScrollReveal — its own world entirely.
const DashboardApp = lazy(() => import("./app/DashboardApp"));

// The owner-only subscriber console (spec 062), served at admin.keepmeout.app. Lazy so its chunk only loads on
// the admin host — the marketing/portal builds never request it. It is a fully self-contained SPA (its own
// password login + state), so it renders BEFORE the Router below, replacing the whole app on that host.
const AdminApp = lazy(() => import("./admin/AdminApp"));

// Initialize Google Analytics
ReactGA.initialize(process.env.REACT_APP_GA_CODE);

const trackPage = (page) => {
  ReactGA.set({ page });
  ReactGA.pageview(page);
};

// The marketing site — UNCHANGED behavior. Wrapped in ScrollReveal + LayoutDefault and
// re-inits scroll-reveal on every marketing navigation, exactly as before.
const MarketingRoutes = () => {
  const childRef = useRef();
  const location = useLocation();

  useEffect(() => {
    const page = location.pathname;
    document.body.classList.add("is-loaded");
    childRef.current.init();
    applyRouteMeta(page);
    handleRouteScroll(location);
    trackPage(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  return (
    <ScrollReveal
      ref={childRef}
      children={() => (
        <Routes>
          <Route
            path="/"
            element={
              <LayoutDefault>
                <Home />
              </LayoutDefault>
            }
          />
          <Route
            path="/Privacy"
            element={
              <LayoutDefault>
                <Privacy />
              </LayoutDefault>
            }
          />
          <Route
            path="/Help"
            element={
              <LayoutDefault>
                <Help />
              </LayoutDefault>
            }
          />
          <Route
            path="/extension"
            element={
              <LayoutDefault>
                <Extension />
              </LayoutDefault>
            }
          />
          <Route
            path="/bring-me-back"
            element={
              <LayoutDefault>
                <BringMeBack />
              </LayoutDefault>
            }
          />
          <Route path="/bringmeback" element={<Navigate to="/bring-me-back" replace />} />
          <Route
            path="*"
            element={
              <LayoutDefault>
                <NotFound />
              </LayoutDefault>
            }
          />
        </Routes>
      )}
    />
  );
};

// The dashboard shell, tracked for analytics but rendered outside the marketing chrome.
const DashboardShell = () => {
  const location = useLocation();
  useEffect(() => {
    trackPage(location.pathname);
  }, [location]);
  return (
    <Suspense fallback={null}>
      <DashboardApp />
    </Suspense>
  );
};

// On the portal.keepmeout.app subdomain the whole site IS the dashboard: the root (and any
// non-/app path) redirects to /app, so portal.keepmeout.app/ lands on the Connect ("enter your
// code") screen. The marketing domain (keepmeout.app) is entirely unaffected. A ?portal=1 query
// lets us exercise this locally, where the hostname isn't portal.*.
function isPortalHost() {
  try {
    // Dashboard-only subdomains: portal.* (live/prod), devportal.* (mock/QA), and staging.*
    // (live against the DEV backend). The marketing domain (keepmeout.app) is unaffected.
    if (/^((dev)?portal|staging)\./i.test(window.location.hostname)) return true;
    return new URLSearchParams(window.location.search).get("portal") === "1";
  } catch (e) {
    return false;
  }
}

// The admin.keepmeout.app subdomain is the owner subscriber console — a separate world from both marketing and
// the portal. In PRODUCTION the console mounts ONLY on the admin.* host: the `?admin=1` escape hatch (a local-dev
// convenience) is disabled in prod builds so it can never surface the owner login on the public marketing apex or
// the portal. CRA inlines process.env.NODE_ENV at build time.
function isAdminHost() {
  try {
    if (/^admin\./i.test(window.location.hostname)) return true;
    return (
      process.env.NODE_ENV !== "production" &&
      new URLSearchParams(window.location.search).get("admin") === "1"
    );
  } catch (e) {
    return false;
  }
}

// Top-level split: /app/* is the dashboard (its own layout). On the portal subdomain everything
// else redirects into /app; on the marketing domain everything else is the marketing site (its
// original ScrollReveal + LayoutDefault behavior, untouched).
export default function App() {
  // The admin console is a self-contained SPA on its own subdomain — short-circuit before the marketing/portal
  // Router entirely, so none of that chrome (or react-router) loads on admin.keepmeout.app.
  if (isAdminHost()) {
    return (
      <Suspense fallback={null}>
        <AdminApp />
      </Suspense>
    );
  }
  const portal = isPortalHost();
  return (
    <Router>
      <Routes>
        <Route path="/app/*" element={<DashboardShell />} />
        <Route
          path="*"
          element={portal ? <Navigate to="/app" replace /> : <MarketingRoutes />}
        />
      </Routes>
    </Router>
  );
}
